import { TZDateMini } from '@date-fns/tz';
import { addMinutes, format, parse, parseISO } from 'date-fns';
import { map, pathOr, pick, replace } from 'ramda';
import { createElement, ReactNode } from 'react';

import ChickFilAOne from '../assets/ChickFilAOne.svg';
import RedMember from '../assets/RedMember.svg';
import SignatureMember from '../assets/SignatureMember.svg';
import SilverMember from '../assets/SilverMember.svg';
import ooeConstants from '../constants';
import { formatDeliveryAddress, formatLongDate, formatPrice } from '../util/format';
import normalizePhone from '../util/normalizePhone';
import {
  CfaLoyalty,
  Customer,
  DashboardOrder,
  DashboardOrderLineItem,
  DashboardOrderModifier,
  OrderReport,
} from './schemas';
import { CombinedDashboardOrder, CurrentTierName, DashboardOrderDisplay } from './types';

export const restaurantStartOfCalendarDay = (date: Date, timeZone: string) =>
  new TZDateMini(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, timeZone);

// Selector helper functions
export function getOrderStatus(status: keyof typeof ooeConstants.GET_STATUS, paymentType: string) {
  let statusName = ooeConstants.GET_STATUS[status];
  let statusIcon = 'success';
  if (statusName === ooeConstants.GET_STATUS.PaymentPending) {
    statusIcon = 'warning';
  } else if (ooeConstants.CANCELLED.includes(status) || status === ooeConstants.ERROR) {
    statusIcon = 'error';
  } else if ((status === 'Submit' || status === 'Ready') && paymentType === 'TO_BE_COLLECTED') {
    statusName = ooeConstants.GET_STATUS.PayLater;
  } else if (
    statusName === ooeConstants.GET_STATUS.Finalized ||
    statusName === ooeConstants.GET_STATUS.Stored
  ) {
    statusIcon = 'restaurant';
  }
  return {
    statusName,
    statusIcon,
  };
}

export function formatDisplayFields(order: DashboardOrder): DashboardOrder & DashboardOrderDisplay {
  return {
    ...order,
    customerName: `${order.givenName} ${order.familyName}`,
    total: order.totalAmount === undefined ? order.subTotalAmount + order.taxAmount : order.totalAmount,
    displayStatus: getOrderStatus(
      order.status as keyof typeof ooeConstants.GET_STATUS,
      pathOr('', ['payment', 'paymentType'], order),
    ),
    displayLongDate: formatLongDate(order.promiseDateTime),
    displaySubtotal: formatPrice(order.subTotalAmount),
    displayPrice:
      order.totalAmount === undefined
        ? formatPrice(order.subTotalAmount + order.taxAmount)
        : formatPrice(order.totalAmount),
    displayClient:
      ooeConstants.GET_CLIENT[order.clientId as keyof typeof ooeConstants.GET_CLIENT] || 'Unknown',
    displayTaxAmount: formatPrice(order.taxAmount),
  };
}

export function calculateModifierQuantity(mod: DashboardOrderModifier, lineItemQuantity: number) {
  const { quantity, itemTag } = mod;
  const toggleable = ooeConstants.TOGGLEABLE_ITEM_TAGS.includes(itemTag);
  // Use line item quantity for toggleable modifiers
  if (toggleable) {
    return lineItemQuantity;
  }
  // Use item quantity for all other modifiers
  return quantity;
}

function calculateModifierPrice(mod: DashboardOrderModifier, lineItemQuantity: number) {
  const { retailPrice, calculatedPrice } = mod;

  return (retailPrice ?? calculatedPrice ?? 0) * lineItemQuantity;
}

export function findComboItem(lineItem: DashboardOrderLineItem, comboItemIndex: number) {
  const { quantity, comboItems } = lineItem;
  if (comboItems) {
    const comboItem = comboItems[comboItemIndex];

    if (comboItem) {
      return {
        ...comboItem,
        price: (comboItem.priceAdjustment ?? 0) * quantity,
        displayQuantity: quantity,
      };
    }
  }
  return undefined;
}

export function findSide(lineItem: DashboardOrderLineItem) {
  return findComboItem(lineItem, 1);
}

export function findPremiumSide(lineItem: DashboardOrderLineItem) {
  return lineItem.comboItems?.length === 4 ? findComboItem(lineItem, 2) : undefined;
}

export function findDessert(lineItem: DashboardOrderLineItem) {
  return findComboItem(lineItem, lineItem.comboItems?.length === 4 ? 3 : 2);
}

export function findModifiers(lineItem: DashboardOrderLineItem) {
  const { quantity } = lineItem;
  if (lineItem.modifiers) {
    return lineItem.modifiers.map((mod) => ({
      ...mod,
      tag: mod.itemTag,
      price: calculateModifierPrice(mod, quantity),
      displayQuantity: calculateModifierQuantity(mod, quantity) ?? 0,
      quantity: mod.quantity ?? 0,
    }));
  }
  return [];
}

export function findComboModifiers(lineItem: DashboardOrderLineItem) {
  const { quantity } = lineItem;
  const comboMods: Array<DashboardOrderLineItem & { tag: string; price: number; displayQuantity: number }> =
    [];
  if (lineItem.comboItems) {
    lineItem.comboItems.forEach((comboItem) => {
      if (comboItem.modifiers) {
        comboItem.modifiers.forEach((comboMod) => {
          comboMods.push({
            ...comboMod,
            tag: comboMod.itemTag,
            price: (comboMod.priceAdjustment || 0) * quantity,
            displayQuantity: calculateModifierQuantity(comboMod, quantity) ?? 0,
            quantity: comboMod.quantity ?? 0,
          });
        });
      }
    });
  }
  return comboMods;
}

export function findPOSModifiers(lineItem: DashboardOrderLineItem) {
  if (lineItem.subItems) {
    return lineItem.subItems.map((subItem) => ({
      ...subItem,
      tag: subItem.itemTag,
      price: subItem.retailPrice,
      displayQuantity: subItem.quantity,
    }));
  }
  return [];
}

export function mapOrderDetails(orderDetails: DashboardOrder) {
  let mappedLineItems;
  if (orderDetails.lineItems) {
    mappedLineItems = orderDetails.lineItems.map((lineItem, idx) => ({
      ...lineItem,
      tag: lineItem.itemTag,
      key: `${lineItem.itemTag}-${idx}`,
      price: (lineItem.retailPrice || 0) * lineItem.quantity,
      compValue: lineItem.compValue,
      promoFree: lineItem.promoFree,
      selectedSide: findSide(lineItem),
      selectedDessert: findDessert(lineItem),
      selectedPremiumSide: findPremiumSide(lineItem),
      modifiers: findPOSModifiers(lineItem)
        .concat(findComboModifiers(lineItem))
        .concat(findModifiers(lineItem)),
    }));
  }
  const additionalFields = formatDisplayFields(orderDetails);
  return {
    ...orderDetails,
    ...additionalFields,
    lineItems: mappedLineItems,
  };
}

export const mapDashboardOrderDetails = (order: OrderReport) => {
  const picked = mapDetailViewOrder(order);
  const addressFields = Object.entries(order.deliveryAddress ?? {}).filter(
    (entry): entry is [string, string | null] => typeof entry[1] !== 'object' || entry[1] === null,
  );
  const address = formatDeliveryAddress(Object.fromEntries(addressFields));
  const phone = normalizePhone(order.user?.phoneNumber || '');
  const timestamps = order.timestamps
    ? map(replacePwoWithWeb, order.timestamps as Record<string, string>)
    : undefined;
  return {
    ...picked,
    deliveryAddress: address,
    totalAmount: Number(order.tranTotal),
    phone: phone,
    timestamps,
  };
};

export const mapDetailViewOrder = (order: OrderReport) => {
  const mapped = pick(
    [
      'id',
      'lineItems',
      'specialInstructions',
      'timestamps',
      'deliveryAddress',
      'companyName',
      'paperGoods',
      'payment',
      'guestCount',
      'totalAmount',
      'deliveryTip',
      'cateringReason',
      'secondaryContact',
      'subTotalAmount',
      'taxAmount',
      'sendCustomerNotifications',
      'user',
      'processLoyalty',
    ],
    order,
  );
  const { deliveryTip, ...rest } = mapped;
  return {
    ...rest,
    ...(mapped.user && { user: { ...mapped.user, phoneNumber: normalizePhone(mapped.user.phoneNumber) } }),
    ...(deliveryTip && {
      deliveryTip: {
        ...(deliveryTip.tipAmount != null && { tipAmount: deliveryTip.tipAmount }),
        ...(deliveryTip.tipPercentOfSale != null && { tipPercentOfSale: deliveryTip.tipPercentOfSale }),
      },
    }),
  };
};

export const mapCustomerDetails = (customer: Customer): Customer => ({
  ...customer,
  phoneNumber: normalizePhone(customer.phoneNumber) || '',
});

export const formatPromisedTime = (promiseDateTime: string) =>
  promiseDateTime ? format(parseISO(promiseDateTime), ooeConstants.DATE_TIME_FORMAT.dashboardOrderTime) : '';

export const formatOrderTime = (time: string) => {
  const parsedDate = parse(time, ooeConstants.DATE_TIME_FORMAT.time, new Date());
  return format(parsedDate, ooeConstants.DATE_TIME_FORMAT.dashboardOrderTime);
};

export const formatDeliveryOrderTime = (time: string) => {
  const startTime = formatOrderTime(time);
  const startDate = parse(time, ooeConstants.DATE_TIME_FORMAT.time, new Date());
  const endDate = addMinutes(startDate, ooeConstants.TIME_SLOT_INTERVAL_IN_MIN);
  const endTime = format(endDate, ooeConstants.DATE_TIME_FORMAT.dashboardOrderTime);
  return `${startTime} - ${endTime}`;
};

export const formatOrderDate = (promiseDateTime: string) => {
  const isoDateTime = parseISO(promiseDateTime);
  return format(isoDateTime, ooeConstants.DATE_TIME_FORMAT.dashboardOrderDate);
};

export const formatAccordionDate = (promiseDateTime: string) =>
  promiseDateTime ? format(parseISO(promiseDateTime), ooeConstants.DATE_TIME_FORMAT.accordionDate) : '';

export const getOrderDayKey = (promiseDateTime: string) => promiseDateTime.slice(0, 10);

export const groupOrdersByDay = (orders: CombinedDashboardOrder[]) => {
  const groups: Record<string, CombinedDashboardOrder[]> = {};
  orders.forEach((order) => {
    if (!order.promiseDateTime) {
      return;
    }
    const day = getOrderDayKey(order.promiseDateTime);
    (groups[day] ??= []).push(order);
  });
  return groups;
};

export const getCfaLoyalty = (tier: CurrentTierName) => {
  if (tier === 'Prospect') {
    return undefined;
  }
  return tier;
};

const loyaltyIcons: Record<CfaLoyalty, { alt: string; src: string }> = {
  'Chick-fil-a One': { alt: 'Chick-fil-A One', src: ChickFilAOne },
  Silver: { alt: 'Silver Member', src: SilverMember },
  Red: { alt: 'Red Member', src: RedMember },
  Signature: { alt: 'Signature Member', src: SignatureMember },
};

export const getLoyaltyIcon = (loyalty: CfaLoyalty, height: number, width: number): ReactNode => {
  const icon = loyaltyIcons[loyalty];
  if (!icon) {
    return null;
  }
  return createElement('img', {
    alt: icon.alt,
    className: 'loyalty-icon',
    height,
    src: icon.src,
    width,
  });
};

export const replacePwoWithWeb = replace('PWO', 'Web');
